(() => {
  const initGallery = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';

    const cards = [...root.querySelectorAll('[data-fv-gallery-card]')];
    const track = root.querySelector('.fv-customer-gallery__track');
    const modal = root.querySelector('[data-fv-gallery-modal]');
    const slider = modal?.querySelector('[data-fv-gallery-slider]');
    const more = root.querySelector('[data-fv-gallery-more]');
    let current = 0;
    let trigger = null;
    let slidesBuilt = false;
    let scrollFrame = 0;
    let railStartX = null;
    let railMoved = false;
    let mouseDrag = null;

    const hiddenThreshold = () => matchMedia('(max-width: 749px)').matches ? 6 : 8;

    if (more) {
      const labelNode = more.childNodes[0];
      const syncMoreVisibility = () => {
        more.hidden = cards.length <= hiddenThreshold();
      };

      syncMoreVisibility();
      more.addEventListener('click', () => {
        const expanded = !root.classList.contains('is-expanded');
        root.classList.toggle('is-expanded', expanded);
        more.setAttribute('aria-expanded', String(expanded));
        if (labelNode) labelNode.textContent = expanded ? 'SHOW FEWER PHOTOS ' : 'SHOW MORE CUSTOMER PHOTOS ';
      });
      window.addEventListener('resize', syncMoreVisibility, { passive: true });
    }

    if (!modal || !slider || !cards.length) return;

    const hydrateAround = (index) => {
      [index - 1, index, index + 1].forEach((slideIndex) => {
        if (slideIndex < 0 || slideIndex >= cards.length) return;
        const image = slider.children[slideIndex]?.querySelector('img[data-src]');
        if (!image || image.hasAttribute('src')) return;
        image.src = image.dataset.src;
      });
    };

    const buildSlides = () => {
      if (slidesBuilt) return;
      const fragment = document.createDocumentFragment();

      cards.forEach((card) => {
        const slide = document.createElement('div');
        const image = document.createElement('img');
        slide.className = 'fv-gallery-modal__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', 'Customer product photo');
        image.dataset.src = card.dataset.full || card.querySelector('img')?.currentSrc || card.querySelector('img')?.src || '';
        image.alt = card.dataset.alt || 'Furavora customer product photo';
        image.width = 420;
        image.height = 753;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.draggable = false;
        slide.append(image);
        fragment.append(slide);
      });

      slider.append(fragment);
      slidesBuilt = true;
    };

    const show = (index, behavior = 'smooth') => {
      current = Math.max(0, Math.min(cards.length - 1, index));
      hydrateAround(current);
      const resolvedBehavior = document.documentElement.dataset.fvMotion === 'reduce' ? 'auto' : behavior;
      slider.scrollTo({ left: current * slider.clientWidth, behavior: resolvedBehavior });
    };

    const syncFromScroll = () => {
      scrollFrame = 0;
      if (!slider.clientWidth) return;
      const next = Math.max(0, Math.min(cards.length - 1, Math.round(slider.scrollLeft / slider.clientWidth)));
      if (next === current) return;
      current = next;
      hydrateAround(current);
    };

    const openModal = (index, card) => {
      trigger = card;
      buildSlides();
      current = index;
      hydrateAround(current);
      modal.showModal();
      document.body.classList.add('fv2-lock');

      requestAnimationFrame(() => {
        show(current, 'auto');
        modal.querySelector('[data-fv-gallery-close]')?.focus({ preventScroll: true });
      });
    };

    track?.addEventListener('pointerdown', (event) => {
      railStartX = event.clientX;
      railMoved = false;
    }, { passive: true });

    track?.addEventListener('pointermove', (event) => {
      if (railStartX === null) return;
      if (Math.abs(event.clientX - railStartX) > 8) railMoved = true;
    }, { passive: true });

    const finishRailGesture = () => {
      railStartX = null;
      setTimeout(() => { railMoved = false; }, 0);
    };

    track?.addEventListener('pointerup', finishRailGesture, { passive: true });
    track?.addEventListener('pointercancel', finishRailGesture, { passive: true });

    cards.forEach((card, index) => {
      card.addEventListener('click', (event) => {
        if (railMoved) {
          event.preventDefault();
          return;
        }
        openModal(index, card);
      });
    });

    modal.querySelector('[data-fv-gallery-close]')?.addEventListener('click', () => modal.close());
    modal.addEventListener('click', (event) => {
      if (event.target === modal) modal.close();
    });
    modal.addEventListener('close', () => {
      document.body.classList.remove('fv2-lock');
      trigger?.focus({ preventScroll: true });
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(current - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(current + 1);
      }
    });

    slider.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(syncFromScroll);
    }, { passive: true });

    slider.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      mouseDrag = {
        id: event.pointerId,
        startX: event.clientX,
        startScroll: slider.scrollLeft
      };
      slider.classList.add('is-dragging');
      slider.setPointerCapture(event.pointerId);
    });

    slider.addEventListener('pointermove', (event) => {
      if (!mouseDrag || event.pointerId !== mouseDrag.id) return;
      slider.scrollLeft = mouseDrag.startScroll - (event.clientX - mouseDrag.startX);
    });

    const finishMouseDrag = (event) => {
      if (!mouseDrag || event.pointerId !== mouseDrag.id) return;
      mouseDrag = null;
      slider.classList.remove('is-dragging');
      show(Math.round(slider.scrollLeft / Math.max(slider.clientWidth, 1)));
    };

    slider.addEventListener('pointerup', finishMouseDrag);
    slider.addEventListener('pointercancel', finishMouseDrag);

    window.addEventListener('resize', () => {
      if (modal.open) show(current, 'auto');
    }, { passive: true });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-fv-customer-gallery]').forEach(initGallery);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
